import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Typography, Box, Slide, Button, useTheme } from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';

import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import SearchBox from '../../components/Common/SearchBox';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import useOrganization from '../../hooks/UseOrganization';
import DeleteDialog from '../../components/Common/DeleteDialog';
import ChangeStatusDialog from '../../components/Common/ChangeStatusDialog';
import CreateEditIDPMapperModal from '../../components/CreateEditIDPMapperModal';
import {
  CommonTableCell,
  StatusCell,
} from '../../components/Common/CommonTableCell';
import utils from '../../utils/Utils';

const tableColumns = [
  {
    heading: 'ID',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'id',
    Cell: CommonTableCell,
    width: 30,
  },
  {
    heading: 'Key',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'key',
    Cell: CommonTableCell,
    width: 150,
    sortType: utils.caseInsensitiveSort,
  },
  {
    heading: 'Value',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'value',
    Cell: CommonTableCell,
    width: 120,
    sortType: utils.caseInsensitiveSort,
  },
  {
    heading: 'Group ID',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'group',
    Cell: CommonTableCell,
    width: 80,
  },
  {
    heading: 'Status',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'is_active',
    Cell: StatusCell,
    sortType: utils.statusSort,
    width: 100,
  },
];

/**
 * @component MappersList
 * @description MappersList - Renders a component for managing identity provider mappings.
 * @returns {JSX.Element} MappersList component.
 */
const MappersList = () => {
  const [isLoading, setIsLoading] = useState(false);

  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();

  const {
    getIdpMappingList,
    createIdpMapping,
    updateIdpMapping,
    deleteIdpMapping,
  } = useOrganization();

  // State management
  const [allMappingList, setAllMappingList] = useState([]); // All data from backend
  const [idpMappingList, setIdpMappingList] = useState([]); // Filtered data for display
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQueryText, setSearchQueryText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateEditModal, setShowCreateEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [currentMapping, setCurrentMapping] = useState(null);

  // Fetch all mapping data once - no pagination parameters
  const fetchIdpMappingList = useCallback(() => {
    setIsLoading(true);
    getIdpMappingList(orgName)
      .then((resp) => {
        const allData = resp?.data?.data || resp?.data || [];
        setAllMappingList(allData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching IDP mapping list:', error);
        setIsLoading(false);
      });
  }, [orgName]);

  const handleCloseStatusModal = useCallback(() => {
    setShowStatusModal(false);
  }, [setShowStatusModal]);

  const refreshMappingList = useCallback(() => {
    fetchIdpMappingList();
  }, [fetchIdpMappingList]);

  const handleChangeStatus = useCallback(() => {
    if (currentMapping) {
      updateIdpMapping(orgName, currentMapping.id, {
        is_active: !currentMapping.is_active,
      }).then(() => {
        setShowStatusModal(false);
        fetchIdpMappingList();
      });
    }
  }, [currentMapping, updateIdpMapping, orgName, fetchIdpMappingList]);

  // Frontend filtering logic only (let table handle pagination)
  const { filteredData, totalCount } = useMemo(() => {
    // Filter data based on search term
    const filtered = allMappingList.filter((item) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        item.key?.toLowerCase().includes(searchLower) ||
        item.value?.toLowerCase().includes(searchLower) ||
        String(item.group)?.toLowerCase().includes(searchLower) ||
        (item.is_active ? 'active' : 'inactive').includes(searchLower)
      );
    });

    return {
      filteredData: filtered,
      totalCount: filtered.length,
    };
  }, [allMappingList, searchTerm]);

  // Update display data when filtered data changes
  useEffect(() => {
    setIdpMappingList(filteredData);
  }, [filteredData]);

  const menus = useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        onClick: ({ props }) => {
          setCurrentMapping(props?.original);
          setMode('edit');
          setShowCreateEditModal(true);
        },
      },
      {
        label: 'Change Status',
        id: 'disable',
        icon: ChangeCircleIcon,
        onClick: ({ props }) => {
          setShowStatusModal(true);
          setCurrentMapping(props?.original);
        },
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        onClick: ({ props }) => {
          setCurrentMapping(props?.original);
          setShowDeleteModal(true);
        },
      },
    ],
    []
  );

  useEffect(() => {
    // Fetch data only once when component mounts
    fetchIdpMappingList();
  }, [fetchIdpMappingList]);

  /**
   * @function debouncedSave
   * @description debounce function to update the search term
   * @returns {void} sets the the searchTerm value
   * @example debouncedSave()
   */
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  /**
   * @function searchMappingByText
   * @description searchMappingByText - Callback function to search mappings by text.
   * @param {Event} e - The event object.
   */
  const searchMappingByText = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchQueryText(value);
      debouncedSave(value);
    },
    [debouncedSave]
  );

  /**
   * @function handleSetSearchText
   * @description handles the input field of searchText.
   * @param {object} searchText - the current text is passed from user for searching.
   * @returns {void} sets the searchText value
   * @example handleSetSearchText(searchText)
   */
  const handleSetSearchText = useCallback((searchText) => {
    setSearchQueryText(searchText);
  }, []);

  /**
   * @function handleDeleteMapping
   * @description handleDeleteMapping - Callback function to handle the deletion of a mapping.
   * @param {Object} currentMapping - The mapping to be deleted.
   */
  const handleDeleteMapping = useCallback(
    async (currentMappingData) => {
      try {
        await deleteIdpMapping(orgName, currentMappingData.id);
        fetchIdpMappingList();
      } catch (error) {
        console.error('Error deleting mapping:', error);
      }
      setShowDeleteModal(false);
    },
    [fetchIdpMappingList, deleteIdpMapping, orgName]
  );

  const handleCreateMapper = useCallback(() => {
    setMode('create');
    setCurrentMapping(null);
    setShowCreateEditModal(true);
  }, []);

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        pt={1}
      >
        <Box width="200px">
          <SearchBox
            value={searchQueryText}
            onChange={searchMappingByText}
            placeholder="Search"
          />
        </Box>
        <Box display="block">
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateMapper}
          >
            + Create Mapper
          </Button>
        </Box>
      </Box>
      <Box flexGrow={1} display="flex" flexDirection="column" mt={1}>
        {isLoading ? (
          <Box
            flexGrow={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            height="20vh"
          >
            <Typography variant="h6" textAlign="center" sx={{ pt: '10px' }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <Slide in={true} direction="up" mountOnEnter timeout={500}>
            {idpMappingList?.length > 0 ? (
              <Box pt={1}>
                <ReactMuiTableListView
                  data={filteredData} // Pass filtered data, let table handle pagination
                  columns={tableColumns}
                  pageCount={Math.ceil(totalCount / 5)} // Calculate based on default page size
                  totalCount={totalCount}
                  getHeaderProps={() => ({
                    style: {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  })}
                  getRowProps={() => ({
                    style: {
                      position: 'relative',
                    },
                  })}
                  setSearchText={handleSetSearchText}
                  enableRowSelection={true}
                  rowLevelRightClickMenus={menus}
                  enableManualFilter={false} // Frontend filtering
                  enableControlledPagination={false} // Let table handle pagination internally
                  dataLength={totalCount}
                  enablePagination={true}
                  initialPageSize={5}
                  rowsPerPageOptions={[5, 10, 15, 20]}
                  skipPageReset={false}
                  searchTerm={searchTerm} // Pass search term for table filtering
                />
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                overflow="auto"
                minHeight="300px"
                height="calc(100vh - 438px)"
              >
                {searchTerm ? (
                  <NoDataImage style={{ color: theme.palette.primary.main }} />
                ) : (
                  <NoDataCreated
                    style={{ color: theme.palette.primary.main }}
                  />
                )}
                <Typography
                  variant="subtitle1"
                  textAlign="center"
                  sx={{ pt: '10px' }}
                >
                  {searchTerm
                    ? 'No matching result found.'
                    : 'No mappings found. Please create a mapping to proceed.'}
                </Typography>
              </Box>
            )}
          </Slide>
        )}
      </Box>

      <ChangeStatusDialog
        open={showStatusModal}
        onCloseClick={handleCloseStatusModal}
        onChangeClick={handleChangeStatus}
        confirmationMsg={`Are you sure you want to change the status of "${currentMapping?.key}" to "${currentMapping?.is_active ? 'Inactive' : 'Active'}"?`}
      />

      <DeleteDialog
        open={showDeleteModal}
        onCloseClick={() => setShowDeleteModal(false)}
        onDeleteClick={() => handleDeleteMapping(currentMapping)}
        confirmationMsg={`Are you sure you want to delete the mapping with key "${currentMapping?.key}"?`}
        resourceName="Claim Mapping"
      />

      <CreateEditIDPMapperModal
        open={showCreateEditModal}
        setOpen={setShowCreateEditModal}
        createIdpMapping={createIdpMapping}
        updateIdpMapping={updateIdpMapping}
        orgName={orgName}
        refreshMappingList={refreshMappingList}
        mode={mode}
        currentMapping={currentMapping}
      />
    </>
  );
};

export default withTransaction('MappersList', 'component')(MappersList);
